import { memo, useCallback, useMemo } from 'react';
import { SingularStationArea } from '@thive/fddm-types';

import { SelectInput } from '@/modules/common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const OPTIONS: { labelKey: string; value: SingularStationArea['connectNeighbors'] }[] = [
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.no',
    value: 'NO_NEIGHBOR_MAPPING',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.curved_forward_only',
    value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.curved_back_and_forth',
    value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_BACK_AND_FORTH',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.pivot_forward_only',
    value: 'MAP_NEIGHBORS_WITH_PIVOTS_FORWARD_ONLY',
  },
  {
    labelKey: 'interface:properties.area.connect_neighbors_options.pivot_back_and_forth',
    value: 'MAP_NEIGHBORS_WITH_PIVOTS_BACK_AND_FORTH',
  },
];

interface Props {
  selectedSingularStationAreas: SingularStationArea[];
}

function SingularConnectNeighborsComponent({ selectedSingularStationAreas }: Props) {
  const optionsOptions = useTranslatedOptions(OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const connectNeighbors = useMemo(
    () => getCommonOption(selectedSingularStationAreas.map((area) => area.connectNeighbors)),
    [selectedSingularStationAreas],
  );

  const onChange = useCallback(
    (newValue: SingularStationArea['connectNeighbors']) => {
      const updatedAreas = createEmptyAreas();
      selectedSingularStationAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, { ...area, connectNeighbors: newValue });
      });
      fddmSvc.updateAreas(updatedAreas);
      fddmSvc.setPreference('singularStationArea.connectNeighbors', newValue);
    },
    [selectedSingularStationAreas, fddmSvc],
  );

  return (
    <SelectInput options={optionsOptions} onChange={onChange} value={connectNeighbors} showMixed />
  );
}

export const SingularConnectNeighbors = memo(SingularConnectNeighborsComponent);
